<template>
    <div class="envio-modal">
        <h3 class="modal-title">Resultado do Envio</h3>

        <div class="response-container">
            <div class="response-message">
                <div class="message-icon" :class="getMessageIconClass()">
                    <component :is="getMessageIcon()" :size="24" />
                </div>
                <div class="message-content">
                    <h4 class="message-title">Resposta da API</h4>
                    <p class="message-text">{{ (aula && aula.mensagem) ? aula.mensagem : 'Processamento concluído' }}</p>
                </div>
            </div>

            <div v-if="aula.codigo && aula.dados && ajuste" class="adjustment-section">
                <div class="adjustment-card">
                    <div class="adjustment-header">
                        <AlertCircle :size="20" color="#f59e0b" />
                        <span class="adjustment-title">Correção Automática Disponível</span>
                    </div>
                    <p class="adjustment-description">
                        Foi detectado um problema que pode ser corrigido automaticamente.
                        Deseja tentar aplicar a correção?
                    </p>
                    <Button
                        text="Aplicar Correção"
                        color="warning"
                        @handle:click="sendAdjustment"
                        :disabled="load"
                        class="adjustment-button"
                    >
                        <Wrench :size="18" />
                    </Button>
                </div>
            </div>

            <div v-if="load" class="loading-container">
                <LoadingSpinner />
                <p>Aplicando correção automática...</p>
            </div>
        </div>

        <div class="modal-actions">
            <Button
                text="Fechar"
                color="secondary"
                :fill="false"
                @handle:click="closeModal"
                :disabled="load"
            >
                <Close :size="18" />
            </Button>
        </div>
    </div>
</template>

<script>
import Vue from 'vue'
import urlApi from '@/router/urlApiV2'

// Componentes Core
import Button from 'core/Button'
import LoadingSpinner from 'core/LoadingSpinner'

// Ícones
import Close from 'icons/Close'
import CheckCircle from 'icons/CheckCircle'
import AlertCircle from 'icons/AlertCircle'
import CloseCircle from 'icons/CloseCircle'
import Wrench from 'icons/Wrench'

export default {
  name: 'EnvioModal',
  components: {
    Button,
    LoadingSpinner,
    Close,
    CheckCircle,
    AlertCircle,
    CloseCircle,
    Wrench
  },
  props: {
    aula: Object,
    ajuste: Boolean
  },
  data () {
    return {
      load: false
    }
  },
  methods: {
    getMessageIcon () {
      if ((this.aula && this.aula.codigo) && this.ajuste) {
        return 'CloseCircle'
      }
      if ((this.aula && this.aula.mensagem && this.aula.mensagem.toLowerCase().includes('sucesso')) ||
                (this.aula && this.aula.mensagem && this.aula.mensagem.toLowerCase().includes('enviado'))) {
        return 'CheckCircle'
      }
      if ((this.aula && this.aula.codigo) || (this.aula && this.aula.mensagem && this.aula.mensagem.toLowerCase().includes('erro'))) {
        return 'AlertCircle'
      }
      return 'CheckCircle'
    },

    getMessageIconClass () {
      if ((this.aula && this.aula.codigo) && this.ajuste) {
        return 'error'
      }
      if ((this.aula && this.aula.mensagem && this.aula.mensagem.toLowerCase().includes('sucesso')) ||
                (this.aula && this.aula.mensagem && this.aula.mensagem.toLowerCase().includes('enviado'))) {
        return 'success'
      }
      if ((this.aula && this.aula.codigo) || (this.aula && this.aula.mensagem && this.aula.mensagem.toLowerCase().includes('erro'))) {
        return 'warning'
      }
      return 'success'
    },

    async sendAdjustment () {
      this.load = true

      try {
        const response = await this.$http.post(`${urlApi}/getran/enviar/ajustar-enviar`, this.aula)

        this.$toasted.show(response.data.mensagem || 'Correção aplicada com sucesso', {
          position: 'top-right',
          type: 'success'
        })

        // Atualizar dados da aula com a resposta
        this.$emit('update:aula', response.data)
      } catch (error) {
        console.error('Erro ao aplicar correção:', error)
        this.$toasted.show('Erro ao aplicar correção automática', {
          position: 'top-right',
          type: 'error'
        })

        // Atualizar com dados de erro se disponível
        if (error.data) {
          this.$emit('update:aula', error.data)
        }
      } finally {
        this.load = false
      }
    },

    closeModal () {
      this.$emit('close')
    }
  }
}
</script>

<style lang="scss" scoped>
.envio-modal {
  max-width: 600px;
  min-width: 500px;
  padding: 2rem; // Compensando o padding do Box removido

  @media (max-width: 768px) {
    min-width: auto;
    max-width: 95vw;
    padding: 1.5rem;
  }
}

.modal-title {
  margin: 0 0 1.5rem 0;
  color: #202224;
  font-size: 1.5rem;
  font-weight: 600;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.response-container {
  margin-bottom: 2rem;
}

.response-message {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.5rem;
  background-color: #f8fafc;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  margin-bottom: 1.5rem;

  .message-icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    &.success {
      background-color: #d1fae5;
      color: #059669;
    }

    &.warning {
      background-color: #fef3c7;
      color: #d97706;
    }

    &.error {
      background-color: #fee2e2;
      color: #dc2626;
    }
  }

  .message-content {
    flex: 1;

    .message-title {
      margin: 0 0 0.5rem 0;
      color: #374151;
      font-size: 1.125rem;
      font-weight: 600;
    }

    .message-text {
      margin: 0;
      color: #6b7280;
      font-size: 0.875rem;
      line-height: 1.5;
    }
  }
}

.adjustment-section {
  .adjustment-card {
    background-color: #fffbeb;
    border: 1px solid #f59e0b;
    border-radius: 0.75rem;
    padding: 1.5rem;

    .adjustment-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 0.75rem;

      .adjustment-title {
        font-weight: 600;
        color: #92400e;
        font-size: 1rem;
      }
    }

    .adjustment-description {
      margin: 0 0 1rem 0;
      color: #78350f;
      font-size: 0.875rem;
      line-height: 1.5;
    }

    .adjustment-button {
      width: 100%;

      @media (min-width: 576px) {
        width: auto;
        min-width: 180px;
      }
    }
  }
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  text-align: center;

  p {
    margin-top: 1rem;
    color: #6b7280;
    font-size: 0.875rem;
  }
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}
</style>
